import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { CaretLeft, SignOut, Phone, Envelope, MapPin, PencilSimple, Wallet, Certificate, Bell, BellSlash, ChatCircle, Briefcase, Star, Flag, WarningOctagon, CurrencyDollar, Moon, SpeakerHigh, Lightning, Image, DeviceMobile } from '@phosphor-icons/react';

const NOTIF_TYPES = [
  { key: 'bid_received', label: 'New Bids', desc: 'When artisans bid on your jobs', icon: Briefcase, color: '#1A4D2E' },
  { key: 'bid_accepted', label: 'Bid Accepted', desc: 'When your bid gets accepted', icon: Lightning, color: '#2D6A4F' },
  { key: 'counter_offer', label: 'Counter Offers', desc: 'Negotiation updates', icon: CurrencyDollar, color: '#E86A33' },
  { key: 'escrow_funded', label: 'Escrow Payments', desc: 'Escrow funded/released', icon: CurrencyDollar, color: '#2D6A4F' },
  { key: 'chat', label: 'Chat Messages', desc: 'New messages from users', icon: ChatCircle, color: '#1A4D2E' },
  { key: 'rating_received', label: 'Ratings', desc: 'When you receive a rating', icon: Star, color: '#F4A261' },
  { key: 'job_match', label: 'Job Matches', desc: 'New jobs matching your trade', icon: Briefcase, color: '#E86A33' },
  { key: 'milestone', label: 'Milestones', desc: 'Milestone updates', icon: Flag, color: '#F4A261' },
  { key: 'dispute', label: 'Disputes', desc: 'Dispute notifications', icon: WarningOctagon, color: '#D00000' },
];

export default function ProfilePage() {
  const { user, token, logout, api, updateUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', state: user?.state || '', lga: user?.lga || '' });
  const [prefs, setPrefs] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);
  const push = usePushNotifications(token);

  useEffect(() => {
    if (user) api.get('/users/notification-prefs').then(({ data }) => setPrefs(data.prefs)).catch(() => {});
  }, [user, api]);

  const handleSave = async () => {
    try { const { data } = await api.patch('/users/profile', form); updateUser(data.user); setEditing(false); } catch {}
  };

  const togglePref = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    try { await api.patch('/users/notification-prefs', { prefs: { [key]: value } }); } catch {}
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (!user) return null;
  const ROLE_LABELS = { customer: 'Customer', artisan: 'Artisan', facility_manager: 'Facility Manager', admin: 'Admin' };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="profile-page">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#C9BAA8]/40 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#DFD3C5] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C]">Profile</h1>
      </header>

      <div className="p-4 space-y-4">
        {/* Hero Card */}
        <div className="bg-gradient-to-br from-[#1A4D2E] to-[#0F3A20] rounded-2xl p-6 relative overflow-hidden shadow-lg shadow-[#1A4D2E]/20">
          <div className="absolute top-[-40px] right-[-40px] w-[160px] h-[160px] bg-white/5 rounded-full" />
          <div className="absolute bottom-[-20px] left-[-20px] w-[100px] h-[100px] bg-[#E86A33]/10 rounded-full" />
          <div className="flex items-center gap-4 mb-4 relative z-10">
            <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm border-2 border-white/20 flex items-center justify-center font-heading text-2xl font-bold text-white">{user.name?.charAt(0)?.toUpperCase()}</div>
            <div>
              <h2 className="font-heading text-xl font-bold text-white">{user.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/15 text-white/90">{ROLE_LABELS[user.role]}</span>
                {user.vetted && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#2D6A4F]/60 text-white/90">Vetted</span>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 relative z-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
              <div className="font-heading text-lg font-bold text-[#F4A261]">{user.trust_score || 0}</div>
              <div className="text-[10px] text-white/60 font-bold">Trust</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
              <div className="font-heading text-lg font-bold text-[#F4A261]">{user.star_rating?.toFixed(1) || '0.0'}</div>
              <div className="text-[10px] text-white/60 font-bold">Rating</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
              <div className="font-heading text-lg font-bold text-white">{user.jobs_completed || 0}</div>
              <div className="text-[10px] text-white/60 font-bold">Jobs</div>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-[#D4C5B3]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C8178]">Contact Details</h3>
            <button onClick={() => setEditing(!editing)} className="text-xs font-bold text-[#E86A33] flex items-center gap-1" data-testid="edit-profile-button"><PencilSimple size={14} />{editing ? 'Cancel' : 'Edit'}</button>
          </div>
          {editing ? (
            <div className="space-y-3">
              <input data-testid="edit-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none focus:border-[#1A4D2E] focus:ring-2 focus:ring-[#1A4D2E]/10" />
              <input data-testid="edit-email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none focus:border-[#1A4D2E]" />
              <input data-testid="edit-state" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="State" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none focus:border-[#1A4D2E]" />
              <input data-testid="edit-lga" value={form.lga} onChange={e => setForm(f => ({ ...f, lga: e.target.value }))} placeholder="LGA" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-lg px-4 py-3 font-body outline-none focus:border-[#1A4D2E]" />
              <button onClick={handleSave} className="w-full bg-[#1A4D2E] text-white font-bold py-3 rounded-lg font-body" data-testid="save-profile-button">Save Changes</button>
            </div>
          ) : (
            <div className="space-y-3">
              {[{ icon: Phone, label: 'Phone', value: user.phone }, { icon: Envelope, label: 'Email', value: user.email || 'Not set' }, { icon: MapPin, label: 'Location', value: user.lga ? `${user.lga}, ${user.state}` : 'Not set' }].map(item => (
                <div key={item.label} className="flex items-center gap-3 py-2 border-b border-[#DDD0C1] last:border-0">
                  <div className="w-8 h-8 bg-[#E8DDD2] rounded-lg flex items-center justify-center"><item.icon size={16} className="text-[#8C8178]" /></div>
                  <div><div className="text-[10px] text-[#8C8178] font-bold">{item.label}</div><div className="text-sm font-semibold text-[#1C1C1C] font-body">{item.value}</div></div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-xl shadow-sm border border-[#D4C5B3] overflow-hidden">
          <button onClick={() => setShowPrefs(!showPrefs)} className="w-full px-4 py-4 flex items-center justify-between" data-testid="toggle-notif-prefs">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#E86A33]/10 rounded-lg flex items-center justify-center"><Bell size={18} className="text-[#E86A33]" /></div>
              <div className="text-left">
                <h3 className="text-sm font-bold text-[#1C1C1C] font-body">Notification Preferences</h3>
                <p className="text-[10px] text-[#8C8178]">Control sounds, alerts & quiet hours</p>
              </div>
            </div>
            <CaretLeft size={16} className={`text-[#8C8178] transition-transform ${showPrefs ? '-rotate-90' : ''}`} />
          </button>

          {showPrefs && prefs && (
            <div className="px-4 pb-4 border-t border-[#DDD0C1] pt-3 space-y-3" data-testid="notif-prefs-panel">
              {/* Global Sound & Flash */}
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2"><SpeakerHigh size={16} className="text-[#1A4D2E]" /><span className="text-sm font-semibold text-[#1C1C1C] font-body">Sound</span></div>
                <button onClick={() => togglePref('sound_enabled', !prefs.sound_enabled)} className={`w-12 h-7 rounded-full transition-all ${prefs.sound_enabled ? 'bg-[#2D6A4F]' : 'bg-[#D4C9BC]'}`} data-testid="toggle-sound">
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.sound_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2"><Lightning size={16} className="text-[#E86A33]" /><span className="text-sm font-semibold text-[#1C1C1C] font-body">Screen Flash</span></div>
                <button onClick={() => togglePref('flash_enabled', !prefs.flash_enabled)} className={`w-12 h-7 rounded-full transition-all ${prefs.flash_enabled ? 'bg-[#2D6A4F]' : 'bg-[#D4C9BC]'}`} data-testid="toggle-flash">
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.flash_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* Quiet Hours */}
              <div className="bg-[#E8DDD2] rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2"><Moon size={16} className="text-[#5C5C5C]" /><span className="text-sm font-semibold text-[#1C1C1C] font-body">Quiet Hours</span></div>
                  <button onClick={() => togglePref('quiet_hours_enabled', !prefs.quiet_hours_enabled)} className={`w-12 h-7 rounded-full transition-all ${prefs.quiet_hours_enabled ? 'bg-[#2D6A4F]' : 'bg-[#D4C9BC]'}`} data-testid="toggle-quiet-hours">
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.quiet_hours_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                {prefs.quiet_hours_enabled && (
                  <div className="flex items-center gap-2 mt-2">
                    <input type="time" value={prefs.quiet_start || '22:00'} onChange={e => togglePref('quiet_start', e.target.value)} className="flex-1 bg-white border border-[#D4C5B3] rounded-lg px-3 py-2 text-sm font-body outline-none" data-testid="quiet-start" />
                    <span className="text-xs text-[#8C8178] font-bold">to</span>
                    <input type="time" value={prefs.quiet_end || '07:00'} onChange={e => togglePref('quiet_end', e.target.value)} className="flex-1 bg-white border border-[#D4C5B3] rounded-lg px-3 py-2 text-sm font-body outline-none" data-testid="quiet-end" />
                  </div>
                )}
                <p className="text-[10px] text-[#8C8178] mt-1">No sounds during quiet hours. Critical alerts always come through.</p>
              </div>

              {/* Per-type toggles */}
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8C8178] pt-2">Notification Types</p>

              {/* Push Notifications */}
              {push.supported && (
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#E86A33]/15"><DeviceMobile size={14} className="text-[#E86A33]" /></div>
                    <div>
                      <span className="text-sm font-semibold text-[#1C1C1C] font-body">Push Notifications</span>
                      <p className="text-[10px] text-[#8C8178]">{push.permission === 'denied' ? 'Blocked by browser' : push.subscribed ? 'Receiving push alerts' : 'Get alerts even when app is closed'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => push.subscribed ? push.unsubscribe() : push.subscribe()}
                    disabled={push.permission === 'denied'}
                    className={`w-11 h-6 rounded-full transition-all ${push.subscribed ? 'bg-[#E86A33]' : 'bg-[#D4C9BC]'} disabled:opacity-40`}
                    data-testid="toggle-push"
                  >
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${push.subscribed ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              )}
              {NOTIF_TYPES.map(t => (
                <div key={t.key} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.color + '15' }}><t.icon size={14} style={{ color: t.color }} /></div>
                    <div>
                      <span className="text-sm font-semibold text-[#1C1C1C] font-body">{t.label}</span>
                      <p className="text-[10px] text-[#8C8178]">{t.desc}</p>
                    </div>
                  </div>
                  <button onClick={() => togglePref(t.key, !prefs[t.key])} className={`w-11 h-6 rounded-full transition-all ${prefs[t.key] ? 'bg-[#2D6A4F]' : 'bg-[#D4C9BC]'}`} data-testid={`toggle-${t.key}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${prefs[t.key] ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Links */}
        {(user.role === 'artisan' || user.role === 'facility_manager') && (
          <div className="space-y-2">
            <button onClick={() => navigate('/portfolio')} className="w-full bg-white border border-[#D4C5B3] text-[#1C1C1C] font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body shadow-sm" data-testid="go-to-portfolio">
              <Image size={18} className="text-[#E86A33]" /> My Portfolio
            </button>
            <button onClick={() => navigate('/wallet')} className="w-full bg-white border border-[#D4C5B3] text-[#1C1C1C] font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body shadow-sm" data-testid="go-to-wallet">
              <Wallet size={18} className="text-[#E86A33]" /> My Wallet
            </button>
            <button onClick={() => navigate('/vetting')} className="w-full bg-white border border-[#D4C5B3] text-[#1C1C1C] font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body shadow-sm" data-testid="go-to-vetting">
              <Certificate size={18} className="text-[#1A4D2E]" /> Verification Status
            </button>
          </div>
        )}

        {user.role === 'admin' && (
          <button onClick={() => navigate('/admin')} className="w-full bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] text-white font-bold py-3 rounded-xl font-body shadow-md" data-testid="go-to-admin">Admin Dashboard</button>
        )}

        <button onClick={handleLogout} className="w-full bg-red-50 border border-red-200 text-red-600 font-bold py-3 rounded-xl flex items-center justify-center gap-2 font-body" data-testid="logout-button">
          <SignOut size={18} /> Sign Out
        </button>
      </div>
    </div>
  );
}
